import { type ReactNode } from 'react';
import type { Creator } from '@/types';
import { Button } from '@/components/ui/button';
import { ExpandableText } from '@/components/ui/expandable-text';
import { useAuth } from '@/hooks/use-auth';
import { AiBadgesRow, StatusBadge } from '@/components/ai/badges';
import type { DetailMode } from './creator-detail-sections';
import { CreatorAppealStatusBlock } from './creator-ai-appeal-status';
import { CreatorAiAdminActions } from './creator-ai-admin-actions';

function PendingNotice({ creator }: { creator: Creator }): ReactNode {
  return (
    <div className="border-t border-white/[0.06] mt-base pt-base">
      <div className="flex flex-wrap items-center gap-sm">
        <StatusBadge creator={creator} />
        <span className="text-xs text-text-tertiary">
          Este lead aún no ha sido analizado por la IA.
        </span>
      </div>
    </div>
  );
}

function AiActions({
  setMode,
}: {
  setMode: (m: DetailMode) => void;
}): ReactNode {
  return (
    <div className="flex flex-wrap items-center gap-sm mt-base">
      <Button variant="primary" size="sm" onClick={() => setMode('appeal')}>
        Apelar
      </Button>
      <Button variant="ghost" size="sm" onClick={() => setMode('user-delete')}>
        Eliminar
      </Button>
    </div>
  );
}

function useActionsState(creator: Creator) {
  const { user, isCollector } = useAuth();
  const isRejected = creator.ai_analysis_status === 'rejected_by_ai';
  const hasAppeal = creator.appeal_status !== 'none' && !!creator.appeal_status;
  const userDeleted = !!creator.user_deleted_at;
  const isOwner = !!user && creator.collector === user.id;
  return {
    isRejected,
    hasAppeal,
    canAct: isCollector && isOwner && !hasAppeal && !userDeleted,
  };
}

function ReasoningBlock({ creator }: { creator: Creator }): ReactNode {
  if (!creator.ai_reasoning) return null;
  return (
    <div className="mt-sm">
      <p className="text-xs font-medium text-text-tertiary">
        Razonamiento de la IA
      </p>
      <ExpandableText content={creator.ai_reasoning} maxChars={240} />
    </div>
  );
}

type Props = {
  creator: Creator;
  setMode: (m: DetailMode) => void;
};

export function CreatorAiSection({ creator, setMode }: Props): ReactNode {
  const a = useActionsState(creator);
  const status = creator.ai_analysis_status;
  if (!status || status === 'pending')
    return <PendingNotice creator={creator} />;
  return (
    <div className="border-t border-white/[0.06] mt-base pt-base">
      <AiBadgesRow creator={creator} />
      <ReasoningBlock creator={creator} />
      {a.hasAppeal && <CreatorAppealStatusBlock creator={creator} />}
      {a.isRejected && a.canAct && <AiActions setMode={setMode} />}
      <CreatorAiAdminActions creator={creator} />
    </div>
  );
}
